import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

export type AppRoute =
  | 'markets'
  | 'trade'
  | 'portfolio'
  | 'orders'
  | 'deposit'
  | 'withdraw'
  | 'economic_calendar'
  | 'news'
  | 'copy_trade'
  | 'analysis'
  | 'settings'

const SIDEBAR_COLLAPSED_KEY = 'velox.sidebar_collapsed'
const ROUTE_STORAGE_KEY = 'velox.route'

const ALL_ROUTES: readonly AppRoute[] = [
  'markets',
  'trade',
  'portfolio',
  'orders',
  'deposit',
  'withdraw',
  'economic_calendar',
  'news',
  'copy_trade',
  'analysis',
  'settings',
]

function isAppRoute(value: string): value is AppRoute {
  return (ALL_ROUTES as readonly string[]).includes(value)
}

function readStoredRoute(): AppRoute {
  try {
    const raw = localStorage.getItem(ROUTE_STORAGE_KEY)
    if (raw && isAppRoute(raw)) return raw
  } catch {
    /* ignore */
  }
  return 'trade'
}

interface NavigationContextValue {
  route: AppRoute
  setRoute: (route: AppRoute) => void
  drawerOpen: boolean
  openDrawer: () => void
  closeDrawer: () => void
  toggleDrawer: () => void
  sidebarCollapsed: boolean
  toggleSidebarCollapsed: () => void
  setSidebarCollapsed: (collapsed: boolean) => void
}

const NavigationContext = createContext<NavigationContextValue | null>(null)

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1'
  } catch {
    return false
  }
}

export function NavigationProvider({ children }: { children: ReactNode }) {
  const [route, setRouteState] = useState<AppRoute>(readStoredRoute)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsedState] = useState(readCollapsed)

  const setRoute = useCallback((next: AppRoute) => {
    setRouteState(next)
  }, [])

  const openDrawer = useCallback(() => setDrawerOpen(true), [])
  const closeDrawer = useCallback(() => setDrawerOpen(false), [])
  const toggleDrawer = useCallback(
    () => setDrawerOpen((open) => !open),
    [],
  )

  const setSidebarCollapsed = useCallback((collapsed: boolean) => {
    setSidebarCollapsedState(collapsed)
  }, [])

  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsedState((c) => !c)
  }, [])

  useEffect(() => {
    try {
      localStorage.setItem(ROUTE_STORAGE_KEY, route)
    } catch {
      /* ignore */
    }
  }, [route])

  useEffect(() => {
    try {
      localStorage.setItem(
        SIDEBAR_COLLAPSED_KEY,
        sidebarCollapsed ? '1' : '0',
      )
    } catch {
      /* ignore */
    }
  }, [sidebarCollapsed])

  const value = useMemo(
    () => ({
      route,
      setRoute,
      drawerOpen,
      openDrawer,
      closeDrawer,
      toggleDrawer,
      sidebarCollapsed,
      toggleSidebarCollapsed,
      setSidebarCollapsed,
    }),
    [
      route,
      setRoute,
      drawerOpen,
      openDrawer,
      closeDrawer,
      toggleDrawer,
      sidebarCollapsed,
      toggleSidebarCollapsed,
      setSidebarCollapsed,
    ],
  )

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  )
}

export function useNavigation(): NavigationContextValue {
  const ctx = useContext(NavigationContext)
  if (!ctx) {
    throw new Error('useNavigation must be used within NavigationProvider')
  }
  return ctx
}
